import { useMemo, memo } from "react";
import type { ToolUIPart } from "../../shared/types";
import { CardShell } from "./shared/CardShell";
import { InlineToolPanel } from "../InlineToolPanel";
import { parseSender, formatFullDate, parseToolOutput } from "./shared/utils";

interface ThreadMessage {
  id?: string;
  from?: string;
  to?: string;
  subject?: string;
  date?: string;
  body_text?: string;
  snippet?: string;
}

interface ParsedThread {
  ok: true;
  thread_id?: string;
  messages: ThreadMessage[];
  subject?: string;
}

interface EmailThreadCardProps {
  part: ToolUIPart;
}

export const EmailThreadCard = memo(function EmailThreadCard({ part }: EmailThreadCardProps) {
  const isStreaming = part.state === "input-streaming";

  const result = useMemo(
    () => parseToolOutput<ParsedThread>(part.state === "output-available" ? part.output : undefined),
    [part.state, part.output],
  );

  if (part.state === "output-available" && result.kind === "parse-failed") {
    return <InlineToolPanel part={part} />;
  }

  const isError = result.kind === "error";
  const messages = result.kind === "ok" ? result.data.messages : [];
  const subject =
    result.kind === "ok"
      ? (result.data.subject ?? result.data.messages?.[0]?.subject ?? "Thread")
      : "Thread";

  const status = isStreaming ? "streaming" : isError ? "error" : "ready";
  const badge = isStreaming ? undefined : isError ? "error" : `${messages.length} message${messages.length !== 1 ? "s" : ""}`;

  return (
    <CardShell
      icon="✉"
      title={subject}
      badge={badge}
      accent={isError ? "error" : "email"}
      status={status}
    >
      {isStreaming && (
        <div className="tool-card__loading">Loading thread...</div>
      )}
      {isError && (
        <div className="tool-card__error-msg">{result.error}</div>
      )}
      {!isStreaming && !isError && messages.length === 0 && (
        <div className="tool-card__empty">No messages in thread.</div>
      )}
      {!isStreaming && !isError && messages.map((msg, i) => {
        const from = msg.from ?? "";
        const date = msg.date ?? "";
        const body = msg.body_text ?? msg.snippet ?? "";
        const senderName = from ? parseSender(from) : "(unknown)";
        const timeStr = date ? formatFullDate(date) : "";

        return (
          <div key={msg.id ?? i} className="tool-card__thread-message" role="article" aria-label={`Message from ${senderName}`}>
            <div className="tool-card__thread-msg-header">
              <span className="tool-card__thread-msg-sender">{senderName}</span>
              {timeStr && (
                <span className="tool-card__thread-msg-time">{timeStr}</span>
              )}
            </div>
            {body && (
              <div className="tool-card__thread-msg-body">{body}</div>
            )}
            {i < messages.length - 1 && (
              <div className="tool-card__thread-divider" aria-hidden="true" />
            )}
          </div>
        );
      })}
    </CardShell>
  );
});
